import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { takeEvery, takeLeading } from 'redux-saga/effects';
import { EventType } from 'stageclip-common';

import { AddEventParticipantRequest } from '../api/definitions/addEventParticipantRequest';
import { AddUnknownEventParticipantRequest } from '../api/definitions/addUnknownEventParticipantRequest';
import { ConvertToUnknownParticipantRequest } from '../api/definitions/convertToUnknownParticipantRequest';
import { CreateGeneratorJobRequest } from '../api/definitions/createGeneratorJobRequest';
import { CreateGeneratorJobRequestForLiveAndHybrid } from '../api/definitions/createGeneratorLiveJobRequest';
import { EditEventParticipantRequest } from '../api/definitions/editEventParticipantRequest';
import { GetAllParticipantsRequest } from '../api/definitions/getAllParticipantsRequest';
import { MergeParticipantsRequest } from '../api/definitions/mergeParticipantsRequest';
import { UploadAegisubResultRequest } from '../api/definitions/uploadAegisubResultRequest';
import { UploadEventParticipantsRequest } from '../api/definitions/uploadEventParticipantsRequest';
import { UploadEventParticipantsWithEventSplit } from '../api/definitions/uploadEventParticipantsWithEventSplit';
import { UploadEventParticipantsMarchingOrderDataRequest } from '../api/definitions/uploadEventParticipantsMarchingOrderDataRequest';
import * as EntityNormalizers from '../api/normalizers/entityNormalizers';
import * as ParticipantNormalizers from '../api/normalizers/participantNormalizers';
import { DataRequestActionTypes, InternalActionTypes } from '../constants/actionTypes';
import { UploadEventParticipantsWithMergeRequest } from '../api/definitions/uploadEventParticipantsWithMergeRequest';

import { UploadEventParticipantsForMarchingOrderRequest } from 'api/definitions/uploadEventParticipantsForMarchingOrderRequest';
import { RequestAction } from './allActions';
import { createAllEventActionsRequestedAction } from './eventActionsActions';
import { FormSubmitAction } from './formActions';
import { runApiRequest, throttleRequests } from './saga/sagaActions';
import { createEventRequestedAction } from './eventActions';
import { CreateRREJobRequestForLiveAndHybrid } from 'api/definitions/createRRELiveJobRequest';

export interface EventParticipantsLoadedAction extends AnyAction {
  type: InternalActionTypes.EventParticipantsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface CreateLiveAndHybridEventParticipantsLoadedAction extends AnyAction {
  type: InternalActionTypes.LiveAndHybridEventParticipantsLoadedAction;
  eventId: string;
}
export interface EventArchivedParticipantsLoadedAction extends AnyAction {
  type: InternalActionTypes.EventArchivedParticipantsLoaded;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface EventParticipantsUploadedAction extends AnyAction {
  type: InternalActionTypes.EventParticipantsUploaded;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface EventParticipantAddedAction extends AnyAction {
  type: InternalActionTypes.EventParticipantAdded;
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface EventParticipantEditedAction extends AnyAction {
  type: InternalActionTypes.EventParticipantEdited;
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface EventParticipantReviewStatusUpdated extends AnyAction {
  type: InternalActionTypes.ParticipantReviewStatusUpdated;
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface ParticipantsMergedAction extends AnyAction {
  type: InternalActionTypes.MergedParticipants;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
  removedParticipantId: string;
  eventId: string;
}

export interface ParticipantConvertedToUnknownParticipantAction extends AnyAction {
  type: InternalActionTypes.ConvertedToUnknownParticipant;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
  eventId: string;
}

export interface UploadParticipantsRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.UploadParticipantsRequested;
  eventId: string;
}

export interface AddParticipantRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.AddParticipantRequested;
  eventId: string;
}

export interface AddUnknownParticipantRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.AddUnknownParticipantRequested;
  eventId: string;
}

export interface EditParticipantRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.EditParticipantRequested;
  id: string;
}

export interface MergeParticipantsRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.MergeParticipantsRequested;
  id: string;
}

export interface ConvertToUknownParticipantRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.ConvertToUnknownParticipantRequested;
  id: string;
}

export interface UploadAegisubResultRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.UploadAegisubResultRequested;
  eventId: string;
}

export interface RequestAllEventParticipantsAction extends RequestAction {
  type: DataRequestActionTypes.AllEventParticipantsRequested;
  eventId: string;
}

export interface RequestEventArchivedParticipantsAction extends RequestAction {
  type: DataRequestActionTypes.EventArchivedParticipantsRequested;
  eventId: string;
}

export interface SendParticipantsToGeneratorRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.SendParticipantsToGeneratorRequested;
  eventId: string;
}

export interface SendLiveParticipantsToGeneratorRequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.SendLiveParticipantsToGeneratorRequested;
  eventId: string;
  eventType: EventType;
}

export interface SendLiveParticipantsToRRERequestedAction extends FormSubmitAction<any> {
  type: DataRequestActionTypes.SendLiveParticipantsToGeneratorRequested;
  eventId: string;
  eventType: EventType;
}

export type RemoveVgQaParticipantAction = {
  type: InternalActionTypes.RemoveVgQaParticipant;
  payload: {
    participantId: string;
  };
};

export type ResetParticipantsListAction = {
  type: InternalActionTypes.ResetParticipantsList;
};

export type ParticipantBlacklistAdded = {
  type: InternalActionTypes.ParticipantBlacklistAdded;
  eventId: string;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
};

export type ParticipantBlacklistRemoved = {
  type: InternalActionTypes.ParticipantBlacklistRemoved;
  eventId: string;
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>;
};

export const resetParticipantsListAction: () => ResetParticipantsListAction = () => ({
  type: InternalActionTypes.ResetParticipantsList,
});

function createEventParticipantsLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>,
): EventParticipantsLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventParticipantsLoaded,
  };
}

function createLiveAndHybridEventParticipantsLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipantsSampleView>,
): CreateLiveAndHybridEventParticipantsLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.LiveAndHybridEventParticipantsLoadedAction,
  };
}

function createEventArchivedParticipantsLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>,
): EventArchivedParticipantsLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventArchivedParticipantsLoaded,
  };
}

function createEventParticipantAddedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>,
): EventParticipantAddedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventParticipantAdded,
  };
}

function createEventParticipantEditedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>,
): EventParticipantEditedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventParticipantEdited,
  };
}

function createParticipantsMergedAction(
  eventId: string,
  removedParticipantId: string,
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>,
): ParticipantsMergedAction {
  return {
    eventId,
    removedParticipantId,
    result,
    type: InternalActionTypes.MergedParticipants,
  };
}

function createConvertedToUnknownParticipantAction(
  eventId: string,
  participantId: string,
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>,
): ParticipantConvertedToUnknownParticipantAction {
  return {
    eventId,
    participantId,
    result,
    type: InternalActionTypes.ConvertedToUnknownParticipant,
  };
}

function createEventParticipantsUploadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>,
): EventParticipantsUploadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventParticipantsUploaded,
  };
}

export function createAllEventParticipantsRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestAllEventParticipantsAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.AllEventParticipantsRequested,
  };
}

export function createEventArchivedParticipantsRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestEventArchivedParticipantsAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.EventArchivedParticipantsRequested,
  };
}

function* uploadEventParticipants(action: UploadParticipantsRequestedAction) {
  const request = new UploadEventParticipantsRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantsUploadedAction(action.eventId, result);
    },
  );
}

function* uploadEventParticipantsWithMerge(action: UploadParticipantsRequestedAction) {
  const request = new UploadEventParticipantsWithMergeRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantsUploadedAction(action.eventId, result);
    },
  );
}

function* uploadEventParticipantsForMarchingOrder(action: UploadParticipantsRequestedAction) {
  const request = new UploadEventParticipantsForMarchingOrderRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantsUploadedAction(action.eventId, result);
    },
  );
}

function* uploadParticipantsWithEventSplit(action: UploadParticipantsRequestedAction) {
  const request = new UploadEventParticipantsWithEventSplit(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantsUploadedAction(action.eventId, result);
    },
  );
}

function* uploadParticipantsMarchingOrderData(action: UploadParticipantsRequestedAction) {
  const request = new UploadEventParticipantsMarchingOrderDataRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantsUploadedAction(action.eventId, result);
    },
  );
}

function* addEventParticipant(action: AddParticipantRequestedAction) {
  const request = new AddEventParticipantRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantAddedAction(action.eventId, result);
    },
  );
}

function* addUnknownEventParticipant(action: AddUnknownParticipantRequestedAction) {
  const request = new AddUnknownEventParticipantRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );
      action.dispatch(createEventRequestedAction(action.eventId, action.requestingComponent, action.dispatch));

      return createEventParticipantAddedAction(action.eventId, result);
    },
  );
}

function* editEventParticipant(action: EditParticipantRequestedAction) {
  const request = new EditEventParticipantRequest(
    action.id,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipants>) =>
      createEventParticipantEditedAction(action.eventId, result),
  );
}

function* getDetailsFromParticipant(action: MergeParticipantsRequestedAction) {
  const request = new MergeParticipantsRequest(
    action.editParticipantData.participant.id,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createParticipantsMergedAction(action.eventId, action.formData.participantToCopyFromId, result);
    },
  );
}

function* convertToUnknownParticipant(action: ConvertToUknownParticipantRequestedAction) {
  const request = new ConvertToUnknownParticipantRequest(action.id, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createConvertedToUnknownParticipantAction(action.eventId, action.id, result);
    },
  );
}

function* uploadAegisubResult(action: UploadAegisubResultRequestedAction) {
  const request = new UploadAegisubResultRequest(
    action.formData.eventVideoId,
    action.formData.aegisubResult,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createEventParticipantsLoadedAction(action.eventId, result);
    },
  );
}

function* loadEventParticipants(action: RequestAllEventParticipantsAction) {
  const request = new GetAllParticipantsRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) =>
      createEventParticipantsLoadedAction(action.eventId, result),
  );
}

function* loadEventArchivedParticipants(action: RequestAllEventParticipantsAction) {
  const request = new GetAllParticipantsRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) =>
      createEventArchivedParticipantsLoadedAction(action.eventId, result),
  );
}

function* sendParticipantsToGenerator(action: SendParticipantsToGeneratorRequestedAction) {
  const request = new CreateGeneratorJobRequest(action.formData, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<ParticipantNormalizers.NormalizedParticipants>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createEventParticipantsLoadedAction(action.eventId, result);
    },
  );
}

function* sendLiveParticipantsToGenerator(action: SendLiveParticipantsToGeneratorRequestedAction) {
  if (action.eventType === EventType.HybridFullCeremonyEvent) {
    const request = new CreateGeneratorJobRequestForLiveAndHybrid(
      { ...action.formData, type: 'Ceremony' },
      action.requestingComponent,
      action.dispatch,
    );
    yield runApiRequest(
      request,
      // @ts-ignore
      () => {
        action.dispatch(
          createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
        );
      },
    );
  }
  const secondRequest = new CreateGeneratorJobRequestForLiveAndHybrid(
    { ...action.formData, type: 'Students' },
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    secondRequest,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipantsSampleView>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createLiveAndHybridEventParticipantsLoadedAction(action.eventId, result);
    },
  );
}

// RRE Function

function* sendLiveParticipantsToRRE(action: SendLiveParticipantsToRRERequestedAction) {
  if (action.eventType === EventType.HybridFullCeremonyEvent) {
    const request = new CreateRREJobRequestForLiveAndHybrid(
      { ...action.formData, type: 'Ceremony' },
      action.requestingComponent,
      action.dispatch,
    );
    yield runApiRequest(
      request,
      // @ts-ignore
      () => {
        action.dispatch(
          createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
        );
      },
    );
  }
  const secondRequest = new CreateRREJobRequestForLiveAndHybrid(
    { ...action.formData, type: 'Students' },
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    secondRequest,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<ParticipantNormalizers.NormalizedParticipantsSampleView>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createLiveAndHybridEventParticipantsLoadedAction(action.eventId, result);
    },
  );
}

export function* uploadEventParticipantsSaga() {
  yield takeEvery(DataRequestActionTypes.UploadParticipantsRequested, uploadEventParticipants);
}

export function* uploadEventParticipantsWithMergeSaga() {
  yield takeEvery(DataRequestActionTypes.UploadParticipantsWithMergeRequested, uploadEventParticipantsWithMerge);
}

export function* uploadEventParticipantsForMarchingOrderSaga() {
  yield takeEvery(DataRequestActionTypes.UploadParticipantsFromMarchingOrder, uploadEventParticipantsForMarchingOrder);
}

export function* uploadParticipantsWithEventSplitSaga() {
  yield takeEvery(DataRequestActionTypes.UploadParticipantsWithEventSplit, uploadParticipantsWithEventSplit);
}

export function* uploadParticipantsMarchingOrderDataSaga() {
  yield takeEvery(DataRequestActionTypes.UploadParticipantsMarchingOrderData, uploadParticipantsMarchingOrderData);
}

export function* addEventParticipantSaga() {
  yield takeEvery(DataRequestActionTypes.AddParticipantRequested, addEventParticipant);
}

export function* addUnknownParticipantSaga() {
  yield takeEvery(DataRequestActionTypes.AddUnknownParticipantRequested, addUnknownEventParticipant);
}

export function* editEventParticipantSaga() {
  yield takeEvery(DataRequestActionTypes.EditParticipantRequested, editEventParticipant);
}

export function* mergeParticipantsSaga() {
  yield takeEvery(DataRequestActionTypes.MergeParticipantsRequested, getDetailsFromParticipant);
}

export function* convertToUnknownParticipantSaga() {
  yield takeEvery(DataRequestActionTypes.ConvertToUnknownParticipantRequested, convertToUnknownParticipant);
}

export function* uploadAegisubResultSaga() {
  yield takeEvery(DataRequestActionTypes.UploadAegisubResultRequested, uploadAegisubResult);
}

export function* loadEventParticipantsSaga() {
  yield throttleRequests(
    DataRequestActionTypes.AllEventParticipantsRequested,
    loadEventParticipants,
    (a: RequestAllEventParticipantsAction) => a.eventId,
  );
}

export function* loadEventArchivedParticipantsSaga() {
  yield throttleRequests(
    DataRequestActionTypes.EventArchivedParticipantsRequested,
    loadEventArchivedParticipants,
    (a: RequestAllEventParticipantsAction) => a.eventId,
  );
}

export function* sendParticipantsToGeneratorSaga() {
  yield takeEvery(DataRequestActionTypes.SendParticipantsToGeneratorRequested, sendParticipantsToGenerator);
}

export function* sendParticipantsToLiveGeneratorSaga() {
  yield takeEvery(DataRequestActionTypes.SendLiveParticipantsToGeneratorRequested, sendLiveParticipantsToGenerator);
}

export function* sendParticipantsToLiveRRESaga() {
  yield takeLeading(DataRequestActionTypes.SendLiveParticipantsToRemotionGeneratorRequested, sendLiveParticipantsToRRE);
}
